import { cn } from '@/lib/utils'

export interface FilterTabOption {
  value: string
  label: string
  /** Nhãn gọn cho điện thoại, ví dụ "Cấp dưới" thay cho "Bảng cước Đơn vị Cấp dưới". */
  shortLabel?: string
  count?: number
}

/**
 * Dải tab lọc. Một kiểu duy nhất cho mọi trang danh sách: gạch chân mục đang
 * mở, không tô nền, không icon, không đổi màu theo ý nghĩa của tab.
 *
 * Trước đây CtvOrders tô nền mỗi tab một màu (tab "Cần cứu đơn" nền cam, các
 * tab khác nền xám, tab đang mở nền xanh) — ba quy ước màu chồng nhau trên
 * cùng một dải, nên màu không còn nói được điều gì.
 *
 * Trên điện thoại KHÔNG cuộn ngang (tab cuối bị cắt, người dùng không biết còn
 * tab): các lựa chọn thành viên tròn tự xuống dòng, thấy hết cùng lúc.
 * Chuyển giữa các KHU VỰC của trang (không phải lọc) thì dùng ViewSwitcher.
 */
export function FilterTabs({
  value,
  onChange,
  options,
  className,
}: {
  value: string
  onChange: (next: string) => void
  options: FilterTabOption[]
  className?: string
}) {
  const count = (o: FilterTabOption, active: boolean) =>
    o.count !== undefined && (
      <span className={cn('ml-1.5 tabular-nums', active ? 'opacity-80' : 'text-muted')}>{o.count}</span>
    )

  return (
    <div className={cn('max-w-full', className)}>
      {/* Máy tính: gạch chân */}
      <div className="mobile:hidden">
        <div className="inline-flex max-w-full flex-wrap gap-1 border-b border-rule">
          {options.map((o) => {
            const active = value === o.value
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={active}
                onClick={() => onChange(o.value)}
                className={cn(
                  'motion-state min-h-11 whitespace-nowrap border-b-2 px-3 text-sm',
                  active ? 'border-accent font-medium text-accent' : 'border-transparent text-inkSoft hover:text-ink',
                )}
              >
                {o.label}
                {count(o, active)}
              </button>
            )
          })}
        </div>
      </div>

      {/* Điện thoại: viên tự xuống dòng */}
      <div className="hidden flex-wrap gap-2 mobile:flex" role="group" data-slot="chips">
        {options.map((o) => {
          const active = value === o.value
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={active}
              aria-label={o.shortLabel ? o.label : undefined}
              onClick={() => onChange(o.value)}
              className={cn(
                'motion-state inline-flex min-h-10 items-center rounded-full px-3.5 text-sm',
                active ? 'bg-accent font-semibold text-onAccent' : 'bg-paperSoft text-inkSoft ring-1 ring-inset ring-rule',
              )}
            >
              <span>{o.shortLabel ?? o.label}</span>
              {count(o, active)}
            </button>
          )
        })}
      </div>
    </div>
  )
}
